import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";
import moment from "moment";

import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { exportCsv } from "../../../utils/exportCsv";
import { paginate, sortRows, toggleSort } from "../../../utils/tableHelpers";

import CadastroPrazoModal from "./CadastroPrazoModal";
import "./styles.css";

// Cobertura do Prazo (PO1-2926, épico PO1-2886): mostra o que falta cadastrar
// na tabela de prazo. Lista as combinações transportadora × destino das NFs do
// período com a contagem por situação; os cards do resumo filtram a tabela
// (abre em "Sem previsão", para o usuário cadastrar a PrazoTabela). Tela no padrão do Hub (PageHero + FilterBand + MetricCardGroup +
// ShellCard/TableSurface). Dados de /trackfy/prazo/cobertura (backend_hub);
// o embarcador ativo vai no header X-Active-Embarcador-Id (services/api).

const DATE_FORMAT = "YYYY-MM-DD";
const PAGE_SIZES = [20, 50, 100];

const buildInitialPeriod = () => ({
  // Últimos 30 dias: a tela é para agir no cadastro de agora (o backend limita a 92 dias).
  de: moment().subtract(30, "days").format(DATE_FORMAT),
  ate: moment().format(DATE_FORMAT),
});

const emptyTotais = {
  total: 0,
  comPrevisao: 0,
  semPrevisao: 0,
  semPagador: 0,
  semTransportadora: 0,
  percentualComPrevisao: 0,
};

const fmtNumber = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(Number(value || 0));
const fmtPercent = (value) =>
  `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(Number(value || 0))}%`;
const fmtDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "-");

// "Vitória/ES"; sem cidade fica só a UF (e vice-versa).
const fmtDestino = (row) => [row.cidade, row.uf].filter(Boolean).join("/") || "-";

const extractError = (requestError, fallback) =>
  (requestError && requestError.response && requestError.response.data && requestError.response.data.error) ||
  (requestError && requestError.message) ||
  fallback;

const EXPORT_COLUMNS = [
  { label: "Embarcador", key: "embarcador" },
  { label: "Transportadora", key: "transportadora" },
  { label: "UF", key: "uf" },
  { label: "Cidade", key: "cidade" },
  { label: "Total de NFs", key: "total" },
  { label: "Com previsão", key: "comPrevisao" },
  { label: "Sem previsão", key: "semPrevisao" },
  { label: "Sem pagador", key: "semPagador" },
  { label: "Retira / entrega própria", key: "semTransportadora" },
];

const TABLE_COLUMNS = [
  { key: "embarcador", label: "Embarcador", sortField: "embarcador" },
  { key: "transportadora", label: "Transportadora", sortField: "transportadora" },
  { key: "destino", label: "Destino", sortField: "destino" },
  { key: "total", label: "Total", sortField: "total", numeric: true },
  { key: "comPrevisao", label: "Com previsão", sortField: "comPrevisao", numeric: true },
  { key: "semPrevisao", label: "Sem previsão", sortField: "semPrevisao", numeric: true },
  { key: "semPagador", label: "Sem pagador", sortField: "semPagador", numeric: true },
  { key: "semTransportadora", label: "Retira / própria", sortField: "semTransportadora", numeric: true },
];

// Cards do resumo = filtro rápido da tabela (padrão do Hub: clicar de novo
// desfaz). "" = todas as combinações. Abre em "Sem previsão": o que falta cadastrar.
const SITUACAO_PADRAO = "semPrevisao";
const SITUACOES = {
  "": { titulo: "Todas as combinações", vazio: "Nenhuma NF no período." },
  comPrevisao: { titulo: "Combinações com previsão", vazio: "Nenhuma NF com previsão no período." },
  semPrevisao: { titulo: "O que falta cadastrar", vazio: "Nenhuma NF sem previsão no período." },
  semPagador: { titulo: "NFs sem pagador", vazio: "Nenhuma NF sem pagador no período." },
  // PO1-3401: não há transportadora para cadastrar prazo.
  semTransportadora: {
    titulo: "Retirada pelo cliente ou entrega própria",
    vazio: "Nenhuma NF de retirada ou entrega própria no período.",
  },
};

// Backend anterior só mandava `faltantes` (sem previsão): vira combinação com as contagens.
const combinacoesDe = (data) => {
  if (!data) return [];
  if (Array.isArray(data.combinacoes)) return data.combinacoes;
  return (data.faltantes || []).map((f) => ({
    ...f, total: f.nfs, comPrevisao: 0, semPrevisao: f.nfs, semPagador: 0, semTransportadora: 0,
  }));
};

// "destino" não existe na linha: ordena pela cidade/UF formatada.
const sortValue = (row, field) => (field === "destino" ? fmtDestino(row) : row[field]);

const rowKey = (row) => [row.idEmbarcador, row.idTransportadora, row.uf, row.idCidade].join("-");

const fieldInputStyle = {
  background: AUTOPILOT_COLORS.inputBg,
  borderColor: AUTOPILOT_COLORS.inputBorder,
  color: AUTOPILOT_COLORS.title,
  boxShadow: "none",
};

const CoberturaPrazo = () => {
  const { empresaId } = useCompanies();

  const [draftPeriod, setDraftPeriod] = useState(buildInitialPeriod);
  const [period, setPeriod] = useState(buildInitialPeriod);
  const [busca, setBusca] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [situacao, setSituacao] = useState(SITUACAO_PADRAO);
  const [sort, setSort] = useState({ field: SITUACAO_PADRAO, dir: "desc" });
  // Linha (transportadora × destino) aberta no modal de cadastro do prazo.
  const [cadastroLinha, setCadastroLinha] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { de: period.de, ate: period.ate };
      // Admin sem embarcador no cabeçalho vê todos; com seleção, filtra.
      if (empresaId) params.embarcador = empresaId;
      const response = await api.get("/trackfy/prazo/cobertura", { params });
      setData(response.data || null);
    } catch (requestError) {
      setData(null);
      setError(extractError(requestError, "Não foi possível consultar a cobertura do prazo."));
    } finally {
      setLoading(false);
    }
  }, [empresaId, period]);

  useEffect(() => {
    load();
  }, [load]);

  // Datas vazias caem no valor já aplicado: o período é obrigatório na API.
  const commitPeriod = (next) => {
    const safe = { de: next.de || period.de, ate: next.ate || period.ate };
    setDraftPeriod(safe);
    setPeriod(safe);
  };
  const handleApply = () => commitPeriod(draftPeriod);
  const handleReset = () => {
    setBusca("");
    escolherSituacao(SITUACAO_PADRAO);
    commitPeriod(buildInitialPeriod());
  };

  // Clicar no card ativo (ou no Total) volta para todas as combinações.
  const escolherSituacao = (proxima) => {
    setSituacao(proxima);
    setSort({ field: proxima || "total", dir: "desc" });
  };
  const alternarSituacao = (chave) => escolherSituacao(situacao === chave ? "" : chave);

  const updateDraft = (field, value) => setDraftPeriod((current) => ({ ...current, [field]: value }));

  const totais = (data && data.totais) || emptyTotais;
  const combinacoes = useMemo(() => combinacoesDe(data), [data]);
  const semFeriado = (data && data.semFeriadoNoAno) || null;

  // Card ativo + busca em memória por embarcador, transportadora, cidade ou UF.
  const filteredRows = useMemo(() => {
    const term = busca.trim().toLowerCase();
    return combinacoes.filter((row) => {
      if (situacao && !(Number(row[situacao]) > 0)) return false;
      if (!term) return true;
      return [row.embarcador, row.transportadora, row.cidade, row.uf].some((value) =>
        String(value || "").toLowerCase().includes(term)
      );
    });
  }, [busca, combinacoes, situacao]);

  const sortedRows = useMemo(() => sortRows(filteredRows, sort, sortValue), [filteredRows, sort]);
  const handleSort = (field) => setSort((current) => toggleSort(current, field));
  const pagina = useMemo(() => paginate(sortedRows, page, pageSize), [sortedRows, page, pageSize]);
  // Card, busca, ordenação ou período novos voltam para a primeira página.
  useEffect(() => {
    setPage(1);
  }, [situacao, busca, sort, data]);

  const handleExport = () =>
    exportCsv({ filename: `cobertura-prazo-${situacao || "todas"}`, columns: EXPORT_COLUMNS, rows: sortedRows });

  const defaultPeriod = useMemo(buildInitialPeriod, []);
  const activeFiltersCount = period.de !== defaultPeriod.de || period.ate !== defaultPeriod.ate ? 1 : 0;

  const percentual = totais.percentualComPrevisao != null
    ? totais.percentualComPrevisao
    : totais.total
    ? (totais.comPrevisao / totais.total) * 100
    : 0;

  return (
    <div className="content cobertura-prazo">
      <PageHero
        eyebrow="Trackfy"
        title="Cobertura do Prazo"
        description="NFs do período sem previsão de entrega, agrupadas por transportadora e destino: é o que falta cadastrar na tabela de prazo."
        actions={
          <>
            <PageHeroRefreshButton onClick={load} loading={loading} />
            <PageHeroExportButton
              onClick={handleExport}
              disabled={!sortedRows.length}
              title="Exportar as combinações da tabela"
            />
          </>
        }
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar"
        searchHint={`Emissão ${fmtDate(period.de)} a ${fmtDate(period.ate)}`}
        searchPlaceholder="Filtrar por embarcador, transportadora, cidade ou UF"
        searchValue={busca}
        onSearchChange={setBusca}
        activeFiltersCount={activeFiltersCount}
        panelTitle="Período"
        panelDescription="Data de emissão das NFs. Padrão: últimos 30 dias; máximo de 92 dias."
        onApply={handleApply}
        onClear={handleReset}
        applyLabel="Buscar"
        busy={loading}
      >
        <Row>
          <Col md="3">
            <FormGroup>
              <Label for="cobertura-de">De</Label>
              <Input
                id="cobertura-de"
                type="date"
                value={draftPeriod.de}
                onChange={(event) => updateDraft("de", event.target.value)}
                style={fieldInputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="cobertura-ate">Até</Label>
              <Input
                id="cobertura-ate"
                type="date"
                value={draftPeriod.ate}
                onChange={(event) => updateDraft("ate", event.target.value)}
                style={fieldInputStyle}
              />
            </FormGroup>
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup
        title="Resumo do período"
        description={
          data && data.periodo
            ? `Emissão ${fmtDate(data.periodo.de)} a ${fmtDate(data.periodo.ate)} · os cards filtram a tabela; clique de novo para desfazer.`
            : "Os cards filtram a tabela; clique de novo para desfazer."
        }
      >
        <MetricCard
          label="Total de NFs"
          value={fmtNumber(totais.total)}
          helper="no período · mostra todas"
          iconClassName="tim-icons icon-notes"
          tone="info"
          active={situacao === ""}
          onClick={() => escolherSituacao("")}
        />
        <MetricCard
          label="Com previsão"
          value={fmtNumber(totais.comPrevisao)}
          helper={`${fmtPercent(percentual)} do total`}
          iconClassName="tim-icons icon-check-2"
          tone="success"
          active={situacao === "comPrevisao"}
          onClick={() => alternarSituacao("comPrevisao")}
        />
        <MetricCard
          label="Sem previsão"
          value={fmtNumber(totais.semPrevisao)}
          helper="sem tabela de prazo para transportadora × destino"
          iconClassName="tim-icons icon-alert-circle-exc"
          tone="warning"
          active={situacao === "semPrevisao"}
          onClick={() => alternarSituacao("semPrevisao")}
        />
        <MetricCard
          label="Sem pagador"
          value={fmtNumber(totais.semPagador)}
          helper="NF sem frete a calcular"
          iconClassName="tim-icons icon-time-alarm"
          tone="neutral"
          active={situacao === "semPagador"}
          onClick={() => alternarSituacao("semPagador")}
        />
        <MetricCard
          label="Retira / entrega própria"
          value={fmtNumber(totais.semTransportadora)}
          helper="sem transportadora: não precisa de prazo"
          iconClassName="tim-icons icon-bus-front-12"
          tone="neutral"
          active={situacao === "semTransportadora"}
          onClick={() => alternarSituacao("semTransportadora")}
        />
      </MetricCardGroup>

      {semFeriado && semFeriado.embarcadores && semFeriado.embarcadores.length > 0 ? (
        <ShellCard>
          <div className="cobertura-prazo__aviso cobertura-prazo__aviso--warning" role="alert">
            <strong>Embarcadores sem feriado cadastrado em {semFeriado.ano}:</strong> o motor conta
            feriado nacional como dia útil para eles.
            <div className="cobertura-prazo__badges">
              {semFeriado.embarcadores.map((item) => (
                <span key={item.idEmbarcador} className="cobertura-prazo__badge cobertura-prazo__badge--warning">
                  {item.embarcador || `#${item.idEmbarcador}`}
                </span>
              ))}
            </div>
          </div>
        </ShellCard>
      ) : null}

      <ShellCard
        title={SITUACOES[situacao].titulo}
        description={`${fmtNumber(sortedRows.length)} combinações transportadora × destino`}
      >
        <TableSurface>
          {error ? (
            <div className="cobertura-prazo__estado cobertura-prazo__estado--erro">{String(error)}</div>
          ) : null}
          {!error && loading ? (
            <div className="cobertura-prazo__estado">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Consultando cobertura...
            </div>
          ) : null}
          {!error && !loading && sortedRows.length === 0 ? (
            <div className="cobertura-prazo__estado">{SITUACOES[situacao].vazio}</div>
          ) : null}

          {!error && !loading && sortedRows.length > 0 ? (
            <Table className="cobertura-prazo__tabela hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  {TABLE_COLUMNS.map((col) => (
                    <th key={col.key} className={col.numeric ? "is-number" : undefined}>
                      <SortHeader
                        label={col.label}
                        field={col.sortField}
                        sort={sort}
                        onSort={handleSort}
                        align={col.numeric ? "right" : "left"}
                      />
                    </th>
                  ))}
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {pagina.items.map((row) => (
                  <tr key={rowKey(row)}>
                    <td>
                      <strong>{row.embarcador || "-"}</strong>
                      <small>#{row.idEmbarcador}</small>
                    </td>
                    <td>
                      <strong>{row.transportadora || "-"}</strong>
                      <small>#{row.idTransportadora}</small>
                    </td>
                    <td className="cobertura-prazo__nowrap">{fmtDestino(row)}</td>
                    {["total", "comPrevisao", "semPrevisao", "semPagador", "semTransportadora"].map((campo) => (
                      <td
                        key={campo}
                        className={`is-number${campo === (situacao || "total") ? " cobertura-prazo__destaque" : ""}`}
                      >
                        {fmtNumber(row[campo])}
                      </td>
                    ))}
                    <td className="cobertura-prazo__nowrap">
                      {Number(row.semPrevisao) > 0 && row.idCidade ? (
                        <Button
                          color="primary"
                          size="sm"
                          className="btn-simple"
                          onClick={() => setCadastroLinha(row)}
                          title="Cadastrar o prazo desta transportadora para este destino"
                        >
                          Cadastrar prazo
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
          {!error && !loading && sortedRows.length > 0 ? (
            <TablePagination
              page={pagina.page}
              pageSize={pageSize}
              total={pagina.total}
              pageSizes={PAGE_SIZES}
              itemLabel="combinação(ões)"
              onPageChange={setPage}
              onPageSizeChange={(novo) => {
                setPageSize(novo);
                setPage(1);
              }}
            />
          ) : null}
        </TableSurface>
      </ShellCard>

      {cadastroLinha ? (
        <CadastroPrazoModal
          linha={cadastroLinha}
          periodo={period}
          onClose={(cadastrou) => {
            setCadastroLinha(null);
            if (cadastrou) load();
          }}
        />
      ) : null}
    </div>
  );
};

export default CoberturaPrazo;
